import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { formatTime } from '../utils/helpers';

// Global audio manager to track currently playing audio
const audioManager = {
  currentPlayer: null,
  setCurrentPlayer(player) {
    // Stop previous player if exists
    if (this.currentPlayer && this.currentPlayer !== player) {
      console.log('Stopping previous audio player');
      // Ensure full stop
      this.currentPlayer.pause();
      this.currentPlayer.currentTime = 0;
    }
    // Set new current player
    this.currentPlayer = player;
  },
  stopAll() {
    if (this.currentPlayer) {
      this.currentPlayer.pause();
      this.currentPlayer.currentTime = 0;
      this.currentPlayer = null;
    }
  }
};

const AudioPlayer = ({ src, fileName, onClose, isVideo = false }) => {
  const mediaRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [downloadStatus, setDownloadStatus] = useState(null);

  // Force stop any other playing media when this component mounts
  useEffect(() => {
    // Stop all other audio
    audioManager.stopAll();
    
    // This will be the current player once loaded
    const media = mediaRef.current;
    
    // Clean up function to stop this player when unmounting
    return () => {
      if (media) {
        media.pause();
        media.currentTime = 0;
        
        // Clear from audio manager when unmounting if it's the current player
        if (audioManager.currentPlayer === media) {
          audioManager.currentPlayer = null;
        }
      }
    };
  }, []);

  useEffect(() => {
    const media = mediaRef.current;
    
    const setMediaData = () => {
      setDuration(media.duration);
      setLoading(false);
      
      // Auto-play once loaded
      media.play().catch(err => {
        console.error(`Error auto-playing ${isVideo ? 'video' : 'audio'}:`, err);
        setError(`Could not auto-play ${isVideo ? 'video' : 'audio'}. Please click play or try downloading the file.`);
      });
      setIsPlaying(true);
      // Register with audio manager
      audioManager.setCurrentPlayer(media);
    };
    
    const setMediaTime = () => setCurrentTime(media.currentTime);
    const handleEnded = () => {
      setIsPlaying(false);
      // Clear from audio manager when ended
      if (audioManager.currentPlayer === media) {
        audioManager.currentPlayer = null;
      }
    };
    
    const handleError = (e) => {
      console.error('Media error:', e);
      setError(`Error loading ${isVideo ? 'video' : 'audio'}. Please try again or download the file.`);
      setLoading(false);
    };

    // Add event listeners
    media.addEventListener('loadeddata', setMediaData);
    media.addEventListener('timeupdate', setMediaTime);
    media.addEventListener('ended', handleEnded);
    media.addEventListener('error', handleError);
    
    // Attempt to load
    media.volume = volume;
    
    // Only load when src changes
    media.load();

    return () => {
      // Clean up event listeners
      media.removeEventListener('loadeddata', setMediaData);
      media.removeEventListener('timeupdate', setMediaTime);
      media.removeEventListener('ended', handleEnded);
      media.removeEventListener('error', handleError);
      
      // Pause media when component unmounts
      media.pause();
      
      // Clear from audio manager when unmounting
      if (audioManager.currentPlayer === media) {
        audioManager.currentPlayer = null;
      }
    };
  }, [src, volume, isVideo]);

  // Separate effect to handle just playback rate changes without reloading
  useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Ensure onClose is called when parent tells component to close
  useEffect(() => {
    // Make this component available to parent through a ref
    const handleForceClose = () => {
      // Stop playback first
      if (mediaRef.current) {
        mediaRef.current.pause();
        mediaRef.current.currentTime = 0;
      }
      
      // Call onClose to notify parent
      if (onClose) onClose();
    };
    
    // Expose the close function to parent components
    if (typeof window !== 'undefined') {
      window._audioPlayerInstances = window._audioPlayerInstances || new Set();
      window._audioPlayerInstances.add(handleForceClose);
      
      return () => {
        window._audioPlayerInstances.delete(handleForceClose);
      };
    }
  }, [onClose]);

  const togglePlay = () => {
    if (isPlaying) {
      mediaRef.current.pause();
      setIsPlaying(false);
    } else {
      // Stop all other audio first
      audioManager.stopAll();
      
      // Register with audio manager before playing
      audioManager.setCurrentPlayer(mediaRef.current);
      
      mediaRef.current.play().catch(err => {
        console.error(`Error playing ${isVideo ? 'video' : 'audio'}:`, err);
        setError(`Could not play ${isVideo ? 'video' : 'audio'}. Please try again or download the file.`);
      });
      setIsPlaying(true);
    }
  };

  const handleProgressClick = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    
    if (mediaRef.current && isFinite(duration) && !isNaN(duration)) {
      mediaRef.current.currentTime = percent * duration;
      setCurrentTime(percent * duration);
    }
  };

  const handleStop = () => {
    mediaRef.current.pause();
    mediaRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
    
    // Clear from audio manager when stopped manually
    if (audioManager.currentPlayer === mediaRef.current) {
      audioManager.currentPlayer = null;
    }
  };

  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Create and click a temporary download link
    const a = document.createElement('a');
    a.href = src;
    a.download = fileName || (isVideo ? 'video.mp4' : 'audio.mp3');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const changePlaybackSpeed = () => {
    // Cycle through speeds: 1.0, 1.25, 1.5, 2.0, 0.5, 0.75
    const speeds = [1.0, 1.25, 1.5, 2.0, 0.5, 0.75];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    
    if (mediaRef.current) {
      // Store current position and state BEFORE making any changes
      const currentPos = mediaRef.current.currentTime;
      const wasPlaying = isPlaying;
      
      // Update playback rate state
      setPlaybackRate(newSpeed);
      
      // Use a callback to ensure all DOM operations happen together
      requestAnimationFrame(() => {
        // Apply new speed
        mediaRef.current.playbackRate = newSpeed;
        
        // Important: Set the time position first, then handle playback
        mediaRef.current.currentTime = currentPos;
        
        // Resume playback if it was playing
        if (wasPlaying) {
          const playPromise = mediaRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(err => {
              console.error('Error resuming playback after speed change:', err);
              setIsPlaying(false);
            });
          }
        }
      });
    }
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  return (
    <PlayerContainer isLoading={loading}>
      {/* <CloseButton onClick={onClose}>
        <i className="fas fa-times"></i>
      </CloseButton> */}
      
      {/* <PlayerTitle>{fileName}</PlayerTitle> */}
      
      {isVideo && (
        <VideoContainer>
          <video 
            ref={mediaRef} 
            src={src} 
            preload="metadata" 
            width="100%" 
            controls={false}
            controlsList="nodownload nofullscreen noremoteplayback"
            disablePictureInPicture
            onClick={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
          />
        </VideoContainer>
      )}
      
      {loading && !error && (
        <LoadingOverlay>
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading {isVideo ? 'video' : 'audio'}...</span>
        </LoadingOverlay>
      )}
      
      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
      
      {downloadStatus && (
        <DownloadStatus>
          {downloadStatus === "Preparing download..." || downloadStatus === "Starting download..." ? (
            <i className="fas fa-spinner fa-spin"></i>
          ) : downloadStatus === "Download complete!" ? (
            <i className="fas fa-check"></i>
          ) : (
            <i className="fas fa-exclamation-circle"></i>
          )}
          {downloadStatus}
        </DownloadStatus>
      )}
      
      <TimeRow>
        <TimeDisplay>{formatTime(currentTime)}</TimeDisplay>
        
        <ProgressContainer onClick={handleProgressClick}>
          <ProgressBar style={{ width: `${progressPercent}%` }} />
        </ProgressContainer>
        
        <TimeDisplay>{formatTime(duration)}</TimeDisplay>
      </TimeRow>
      
      <ControlsRow>
        <ControlsLeft>
          <ControlButton onClick={onClose} title="Close">
            <i className="fas fa-times"></i>
          </ControlButton>
          
          <PlayButton onClick={togglePlay} disabled={loading || error}>
            <i className={isPlaying ? "fas fa-pause" : "fas fa-play"}></i>
          </PlayButton>
          
          <ControlButton onClick={handleStop} title="Stop">
            <i className="fas fa-stop"></i>
          </ControlButton>
        </ControlsLeft>
        
        <ControlsRight>
          <SpeedButton onClick={changePlaybackSpeed} disabled={loading || error}>
            {playbackRate}x
          </SpeedButton>
          
          <ControlButton onClick={handleDownload} title="Download">
            <i className="fas fa-download"></i>
          </ControlButton>
        </ControlsRight>
      </ControlsRow>
      
      {!isVideo && (
        <audio 
          ref={mediaRef} 
          src={src} 
          preload="metadata"
          controls={false}
          controlsList="nodownload nofullscreen noremoteplayback"
          onClick={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
        />
      )}
    </PlayerContainer>
  );
};

// Styled Components
const PlayerContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  position: relative;
  opacity: ${props => props.isLoading ? 0.8 : 1};
  transition: opacity 0.3s ease;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  z-index: 10;
  
  i {
    font-size: 24px;
    color: #0047ba;
    margin-bottom: 10px;
  }
  
  span {
    color: #6c757d;
    font-weight: 500;
  }
`;

const VideoContainer = styled.div`
  margin-bottom: 15px;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  
  video {
    display: block;
    width: 100%;
    max-height: 240px;
    pointer-events: none;  /* Prevent clicks from triggering native controls */
  }
  
  /* Overlay to catch and handle click events instead of the video element */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: var(--text-light);
  transition: var(--transition);
  
  &:hover {
    color: var(--error-color);
  }
`;

const PlayerTitle = styled.div`
  font-weight: 500;
  margin-bottom: 15px;
  padding-right: 30px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 5px;
`;

const TimeDisplay = styled.span`
  font-size: 12px;
  color: #6c757d;
  flex-shrink: 0;
`;

const ProgressContainer = styled.div`
  flex: 1;
  height: 6px;
  background-color: #e9ecef;
  border-radius: 3px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 20px;
`;

const ProgressBar = styled.div`
  height: 100%;
  background-color: #0047ba;
  border-radius: 3px;
  transition: width 0.1s linear;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 25px;
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
`;

const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
`;

const ControlButton = styled.button`
  background-color: #fff;
  color: #0047ba;
  border: 2px solid #0047ba;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  
  &:hover:not(:disabled) {
    background-color: rgba(0, 71, 186, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PlayButton = styled(ControlButton)`
  background-color: ${props => props.isPlaying ? '#fff' : '#0047ba'};
  color: ${props => props.isPlaying ? '#0047ba' : '#fff'};
  &:hover:not(:disabled) {
    background-color: ${props => props.isPlaying ? 'rgba(0, 71, 186, 0.1)' : '#003da5'};
  }
`;

const SpeedButton = styled(ControlButton)`
  font-size: 14px;
  font-weight: bold;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 10px;
  text-align: center;
`;

const DownloadStatus = styled.div`
  color: var(--primary-color);
  background-color: rgba(0, 71, 186, 0.1);
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 10px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  i {
    font-size: 16px;
  }
`;

export default AudioPlayer; 